import { Translations } from "@aidkitorg/i18n/lib"

// This will get you a readable payment method name from a payment type string.
// Method is different than payment kind - for example, givecard_load would be the payment kind, 
// but the payment method could either be a physical card or a virtual card.
export const paymentToReadablePaymentMethod = (paymentType: string, L: Translations) => {
  if (paymentType.includes('ach')) {
    return 'Dwolla ACH';
  }
  if (paymentType === 'givecard_rtp') {
    return 'Givecard Real-time Payments';
  }
  if (paymentType === 'usbank_zelle') {
    return 'USBank Zelle';
  }
  const cardType = paymentType.includes('usio') 
    ? 'USIO' 
    : paymentType.includes('givecard') 
      ? 'Givecard' : paymentType.includes('usbank') ? 'USBank' : 'Unknown';
  if (paymentType.includes('mail') || paymentType.includes('pickup')) {
    return L.payments.physical_card.replace('$type', cardType);
  }
  if (paymentType.includes('virtual')) {
    return L.payments.virtual_card.replace('$type', cardType);
  }
  if (paymentType.includes('load') || cardType !== 'Unknown') {
    return L.payments.generic_card.replace('$type', cardType);
  }
  return L.payments.not_yet_selected;
}

// The display name for a payment row: the configured payment name, falling
// back to a title-cased target field, then a generic "Payment". Shared by the
// staff payment page and the applicant portal so both show the same name.
// An attempt > 1 appends an "(attempt N)" suffix (staff only — the applicant
// portal passes 1, since retry counts aren't applicant-facing).
export function getPaymentName(name: string | undefined, targetField: string | undefined, attempt: number, L: Translations) {
  const nameToUse = name ?? prettifyTargetField(targetField, L);

  if (attempt > 1) {
    const attemptText = L.payments.attempt.replace('$n', attempt.toString());
    return `${nameToUse} ${attemptText}`;
  } else {
    return nameToUse;
  }
}

export function prettifyTargetField(targetField: string | undefined, L: Translations) {
  if (!targetField) {
    return L.applicant.payment;
  }

  return targetField
    .split('_')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

// Readable payment kind name from a payment type string.
export const paymentTypeToString = (paymentType: string, withdrawal?: boolean) => {
  switch(paymentType) {
    case 'ach':
    case 'dwolla_float_ach_grant':
      return 'ACH'
    case 'sameday_ach':
      return 'Same-Day ACH'
    case 'usio_mail':
    case 'usio_mailed_grant':
      return 'USIO Mailed Card'
    case 'usio':
    case 'usio_pickup':
    case 'usio_virtual':
    case 'usio_load':
    case 'load_usio_card':
      return withdrawal ? 'USIO Card Withdrawal' : 'USIO Card Load'
    case 'western_union_grant':
      return 'Western Union Grant'
    case 'screener_payment':
      return 'Screener Payment'
    case 'givecard_mail':
      return 'Givecard Mailed Card'
    case 'givecard_rtp':
      return 'Givecard Real-time Payment'
    case 'usbank_card_mail':
      return 'US Bank Mailed Card'
    case 'usbank_card_load':
      return withdrawal ? 'US Bank Card Withdrawal' : 'US Bank Card Load'
    case 'usbank_zelle':
      return 'US Bank Zelle'
    case 'givecard_virtual':
      return 'Givecard Virtual Card'
    case 'givecard_load':
      return withdrawal ? 'Givecard Card Withdrawal' : 'Givecard Card Load'
    default:
      return paymentType;
  }
}
